// UsersTab.js
// Manages users: create, edit, activate/deactivate, reset password, revoke MFA,
// and admin lock/unlock.

import React, { useState, useEffect } from 'react';
import { IconLock, IconLockOpen } from '@tabler/icons-react';
import api from '../../services/api';
import Button from '../../components/Button';
import {
  CREDENTIAL_ROLE_MAP,
  ROLE_DISPLAY,
  ROLE_ORDER,
  CREDENTIALS,
  ROLES,
  styles,
} from './settingsShared';

// ---------------------------------------------------------------------------
// Lock state helpers
// ---------------------------------------------------------------------------

function getLockState(u) {
  if (u.admin_locked) return 'admin';
  if (u.locked_until && new Date(u.locked_until) > new Date()) return 'system';
  return 'none';
}

// ---------------------------------------------------------------------------
// PadlockCell
// ---------------------------------------------------------------------------

function PadlockCell({ u, onLock, onUnlock, lockingId, unlockingId }) {
  const lockState = getLockState(u);
  const isLoading = lockingId === u.id || unlockingId === u.id;
  const isAdmin = u.role === 'admin';

  const config = {
    none: {
      Icon: IconLockOpen,
      color: isAdmin ? '#858e96' : '#000000',
      title: isAdmin ? 'Admin accounts cannot be locked' : 'Click to lock this account',
      onClick: isAdmin ? undefined : () => onLock(u),
    },
    system: {
      Icon: IconLock,
      color: '#d97706',
      title: 'Locked due to failed login attempts — click to unlock',
      onClick: () => onUnlock(u),
    },
    admin: {
      Icon: IconLock,
      color: '#dc2626',
      title: 'Locked by administrator — click to unlock',
      onClick: () => onUnlock(u),
    },
  };

  const { Icon, color, title, onClick } = config[lockState];

  return (
    <td style={styles.td}>
      <button
        onClick={onClick}
        disabled={isLoading || (lockState === 'none' && isAdmin)}
        title={title}
        style={{
          background: 'none',
          border: 'none',
          cursor: (isLoading || (lockState === 'none' && isAdmin)) ? 'default' : 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: (lockState === 'none' && isAdmin) ? 0.3 : isLoading ? 0.4 : 1,
          borderRadius: '4px',
        }}
        aria-label={title}
      >
        <Icon size={18} stroke={1.5} color={color} />
      </button>
    </td>
  );
}

// ---------------------------------------------------------------------------
// UserRow
// ---------------------------------------------------------------------------

function UserRow({ u, onEdit, onPasswordReset, onRevokeMfa, onToggleActive,
                   onLock, onUnlock, resetingId, revokingId, lockingId, unlockingId }) {
  return (
    <tr style={styles.tr}>
      <td style={styles.td}>
        {u.first_name} {u.last_name}{u.credential ? `, ${u.credential}` : ''}
      </td>
      <td style={styles.td}>{ROLE_DISPLAY[u.role] || u.role}</td>
      <td style={styles.td}>
        <span style={u.mfa_enabled ? styles.badgeMfaOn : styles.badgeMfaOff}>
          {u.mfa_enabled ? 'Enrolled' : 'Not enrolled'}
        </span>
      </td>
      <PadlockCell
        u={u}
        onLock={onLock}
        onUnlock={onUnlock}
        lockingId={lockingId}
        unlockingId={unlockingId}
      />
      <td style={styles.td}>
        <span style={u.is_active ? styles.badgeActive : styles.badgeInactive}>
          {u.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td style={styles.td}>
        <div style={styles.actionGroup}>
          <Button variant="neutral" size="sm" onClick={() => onEdit(u)}>
            Edit
          </Button>
          <Button
            variant="neutral"
            size="sm"
            onClick={() => onPasswordReset(u.id)}
            disabled={resetingId === u.id}
          >
            {resetingId === u.id ? 'Sending...' : 'Reset Password'}
          </Button>
          {u.mfa_enabled && (
            <Button
              variant="caution"
              size="sm"
              onClick={() => onRevokeMfa(u.id)}
              disabled={revokingId === u.id}
            >
              {revokingId === u.id ? 'Revoking...' : 'Revoke MFA'}
            </Button>
          )}
          <Button
            variant={u.is_active ? 'caution' : 'neutral'}
            size="sm"
            onClick={() => onToggleActive(u)}
          >
            {u.is_active ? 'Deactivate' : 'Activate'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// UsersTab
// ---------------------------------------------------------------------------

export default function UsersTab({ users, onRefresh, defaultEmailDomain = '' }) {
  const emptyForm = {
    first_name: '', last_name: '', credential: '', role: '',
  };

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [emailLocal, setEmailLocal] = useState('');
  const [emailManuallyEdited, setEmailManuallyEdited] = useState(false);

  const [showInactive, setShowInactive] = useState(false);
  const [resetingId, setResetingId] = useState(null);
  const [resetMessage, setResetMessage] = useState('');
  const [revokingId, setRevokingId] = useState(null);
  const [lockingId, setLockingId] = useState(null);
  const [unlockingId, setUnlockingId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    if (editingId || emailManuallyEdited || !defaultEmailDomain) return;
    const first = form.first_name.trim();
    const last = form.last_name.trim().replace(/\s/g, '');
    if (first && last) {
      setEmailLocal(`${first[0].toLowerCase()}${last.toLowerCase()}`);
    } else if (last) {
      setEmailLocal(last.toLowerCase());
    }
  }, [form.first_name, form.last_name, editingId, emailManuallyEdited, defaultEmailDomain]);

  const sortedUsers = [...users].sort((a, b) => {
    const roleDiff = (ROLE_ORDER[a.role] ?? 99) - (ROLE_ORDER[b.role] ?? 99);
    if (roleDiff !== 0) return roleDiff;
    return a.last_name.localeCompare(b.last_name);
  });

  const filteredUsers = sortedUsers.filter(u => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = !q || (
      u.first_name.toLowerCase().includes(q) ||
      u.last_name.toLowerCase().includes(q) ||
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q)
    );
    const matchesRole = !roleFilter || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const activeUsers = filteredUsers.filter(u => u.is_active);
  const inactiveUsers = filteredUsers.filter(u => !u.is_active);
  const isFiltering = searchQuery.trim() !== '' || roleFilter !== '';

  const TABLE_HEADERS = ['Name', 'Role', 'MFA', 'Lock', 'Status', 'Actions'];

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setEmailLocal('');
    setEmailManuallyEdited(false);
    setError('');
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      credential: u.credential || '',
      role: u.role || '',
    });
    setEmailLocal(u.email || '');
    setEmailManuallyEdited(true);
    setError('');
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setEmailLocal('');
    setEmailManuallyEdited(false);
    setError('');
  };

  const handleCredentialChange = (credential) => {
    const autoRole = CREDENTIAL_ROLE_MAP[credential] || '';
    setForm(prev => ({ ...prev, credential, role: autoRole }));
  };

  const handleSave = async () => {
    const fullEmail = emailLocal.includes('@')
      ? emailLocal
      : defaultEmailDomain
        ? `${emailLocal}@${defaultEmailDomain}`
        : emailLocal;

    if (!form.first_name || !form.last_name || !fullEmail || !form.credential) {
      setError('First name, last name, email, and credential are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      if (editingId) {
        await api.patch(`/users/${editingId}`, {
          first_name: form.first_name,
          last_name: form.last_name,
          credential: form.credential || null,
          email: fullEmail,
          role: form.role || null,
        });
      } else {
        await api.post('/users/', {
          first_name: form.first_name,
          last_name: form.last_name,
          credential: form.credential || null,
          email: fullEmail,
          role: form.role || null,
        });
      }
      cancelForm(); onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save user.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      await api.patch(`/users/${user.id}`, { is_active: !user.is_active });
      onRefresh();
    } catch {}
  };

  const handlePasswordReset = async (userId) => {
    setResetingId(userId); setResetMessage('');
    try {
      const userObj = users.find(u => u.id === userId);
      await api.post('/auth/password-reset/request', { username: userObj.username });
      setResetMessage('Password reset email sent.');
    } catch {
      setResetMessage('Failed to send reset email.');
    } finally {
      setResetingId(null);
    }
  };

  const handleRevokeMfa = async (userId) => {
    if (!window.confirm('Revoke MFA for this user? They will need to re-enroll on next login.')) return;
    setRevokingId(userId);
    try {
      await api.delete(`/mfa/users/${userId}/enrollment`);
      onRefresh();
    } catch {
    } finally {
      setRevokingId(null);
    }
  };

  const handleLock = async (user) => {
    if (!window.confirm(
      `Lock ${user.first_name} ${user.last_name}'s account? They will be unable to log in until unlocked.`
    )) return;
    setLockingId(user.id);
    try {
      await api.post(`/users/${user.id}/lock`);
      onRefresh();
    } catch (err) {
      setResetMessage(err.response?.data?.detail || 'Failed to lock account.');
    } finally {
      setLockingId(null);
    }
  };

  const handleUnlock = async (user) => {
    setUnlockingId(user.id);
    try {
      await api.post(`/users/${user.id}/unlock`);
      onRefresh();
    } catch (err) {
      setResetMessage(err.response?.data?.detail || 'Failed to unlock account.');
    } finally {
      setUnlockingId(null);
    }
  };

  return (
    <div>
      <div style={styles.tabHeader}>
        <h2 style={styles.sectionTitle}>Users</h2>
        <Button onClick={showForm ? cancelForm : openAdd} variant={showForm ? 'secondary' : 'primary'}>
          {showForm ? 'Cancel' : '+ Add User'}
        </Button>
      </div>

      {/* Search and filter bar */}
      <div style={filterStyles.bar}>
        <input
          type="text"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={filterStyles.searchInput}
        />
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          style={filterStyles.roleSelect}
        >
          <option value="">All Roles</option>
          {ROLES.map(r => (
            <option key={r} value={r}>{ROLE_DISPLAY[r]}</option>
          ))}
        </select>
      </div>

      {resetMessage && <div style={styles.messageBanner}>{resetMessage}</div>}

      {showForm && (
        <div style={styles.form}>
          <div style={styles.formTitle}>{editingId ? 'Edit User' : 'New User'}</div>
          {error && <div style={styles.errorText}>{error}</div>}
          <div style={styles.formGrid}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>First Name *</label>
              <input
                style={styles.input}
                value={form.first_name}
                onChange={e => setForm({ ...form, first_name: e.target.value })}
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Last Name *</label>
              <input
                style={styles.input}
                value={form.last_name}
                onChange={e => setForm({ ...form, last_name: e.target.value })}
              />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Credential *</label>
              <select
                style={styles.input}
                value={form.credential}
                onChange={e => handleCredentialChange(e.target.value)}
              >
                <option value="">— Select —</option>
                {CREDENTIALS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Role</label>
              <select
                style={styles.input}
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
              >
                <option value="">— Select —</option>
                {ROLES.map(r => <option key={r} value={r}>{ROLE_DISPLAY[r]}</option>)}
              </select>
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Email *</label>
              {defaultEmailDomain && !emailLocal.includes('@') ? (
                <div style={emailStyles.wrapper}>
                  <input
                    style={emailStyles.localInput}
                    type="text"
                    value={emailLocal}
                    onChange={e => {
                      setEmailLocal(e.target.value);
                      setEmailManuallyEdited(true);
                    }}
                    placeholder="e.g. bkrull"
                  />
                  <span style={emailStyles.domain}>@{defaultEmailDomain}</span>
                </div>
              ) : (
                <input
                  style={styles.input}
                  type="email"
                  value={emailLocal}
                  onChange={e => {
                    setEmailLocal(e.target.value);
                    setEmailManuallyEdited(true);
                  }}
                  placeholder="full@email.com"
                />
              )}
            </div>
          </div>
          <div style={styles.formActions}>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create User'}
            </Button>
            <Button variant="secondary" onClick={cancelForm}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Active users */}
      <table style={styles.table}>
        <thead>
          <tr>{TABLE_HEADERS.map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {activeUsers.length === 0 && (
            <tr>
              <td colSpan={6} style={styles.emptyRow}>
                {isFiltering ? 'No users match your search.' : 'No active users.'}
              </td>
            </tr>
          )}
          {activeUsers.map(u => (
            <UserRow
              key={u.id} u={u}
              onEdit={openEdit}
              onPasswordReset={handlePasswordReset}
              onRevokeMfa={handleRevokeMfa}
              onToggleActive={toggleActive}
              onLock={handleLock}
              onUnlock={handleUnlock}
              resetingId={resetingId}
              revokingId={revokingId}
              lockingId={lockingId}
              unlockingId={unlockingId}
            />
          ))}
        </tbody>
      </table>

      {/* Inactive users (collapsed by default) */}
      {inactiveUsers.length > 0 && (
        <div style={styles.inactiveSection}>
          <button
            style={styles.showInactiveToggle}
            onClick={() => setShowInactive(v => !v)}
          >
            {showInactive ? '▾' : '▸'}{' '}
            {showInactive ? 'Hide' : 'Show'} inactive users ({inactiveUsers.length})
          </button>
          {showInactive && (
            <table style={{ ...styles.table, marginTop: '12px' }}>
              <thead>
                <tr>{TABLE_HEADERS.map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {inactiveUsers.map(u => (
                  <UserRow
                    key={u.id} u={u}
                    onEdit={openEdit}
                    onPasswordReset={handlePasswordReset}
                    onRevokeMfa={handleRevokeMfa}
                    onToggleActive={toggleActive}
                    onLock={handleLock}
                    onUnlock={handleUnlock}
                    resetingId={resetingId}
                    revokingId={revokingId}
                    lockingId={lockingId}
                    unlockingId={unlockingId}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// Filter bar styles
const filterStyles = {
  bar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    alignItems: 'center',
  },
  searchInput: {
    padding: '7px 10px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    width: '220px',
    backgroundColor: 'var(--color-bg-card)',
    fontFamily: 'var(--font-family-base)',
  },
  roleSelect: {
    padding: '7px 10px',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    backgroundColor: 'var(--color-bg-card)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family-base)',
  },
};

// Email field styles
const emailStyles = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    backgroundColor: 'var(--color-bg-card)',
    overflow: 'hidden',
  },
  localInput: {
    padding: '8px 10px',
    border: 'none',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    flex: 1,
    backgroundColor: 'var(--color-bg-card)',
    minWidth: 0,
    fontFamily: 'var(--font-family-base)',
  },
  domain: {
    padding: '8px 10px 8px 4px',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    whiteSpace: 'nowrap',
    backgroundColor: 'var(--color-bg-card)',
    fontFamily: 'var(--font-family-base)',
  },
};